import { useCallback, useState } from 'react';
import { ArrowLeft, ArrowRight, Warning } from 'shared/icons';
import api from '../../../services/api';
import { FormatInt } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import { } from '../warehouseLabels';
import BlockTower from './BlockTower';
import {
    BigAction,
    CodeCard,
    CodeLine,
    DangerScreenAction,
    FailureScreen,
    PlainAddress,
    ScanFlash,
    StepShell,
    SuccessScreen,
} from './CollectorChrome';
import ScanInput from './ScanInput';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from './collectorFeedback';
import {
    BuildRackStagingCode,
    CodesMatch,
    FormatLocationPlain,
    INCIDENT_KIND_MISSING_PALLET,
    INCIDENT_KIND_SCAN_MISMATCH,
    INCIDENT_KIND_UNREGISTERED_OCCUPANCY,
    ParseEngineBlocks,
    ParseScanResult,
    SCAN_METHOD_SCANNED,
    SCAN_METHOD_TYPED,
    SCAN_TYPE_BLOCK,
    SCAN_TYPE_LOCATION,
    SCAN_TYPE_LPN,
} from './collectorScan';

/**
 * MOVER PALLET — a cadeia de bipes guiada do M4 (§5 do contrato, PRD §8.2).
 *
 * ── A cadeia ────────────────────────────────────────────────────────────────
 *   1. bipar a posição de ORIGEM   2. bipar o LPN   3. bipar o DESTINO
 * e, no fim, uma tela de conferência antes de gravar. Três passos, um por
 * tela, cada um com o título grande da ação e o endereço em texto claro.
 *
 * ── Onde mora o "código ESPERADO" ───────────────────────────────────────────
 * Movimento avulso não vem de rota: no passo 1 o operador bipa ONDE ELE ESTÁ,
 * então não há o que esperar — a leitura é que DEFINE a origem (e o
 * classificador do backend é quem diz se aquilo é posição, bloco ou LPN;
 * §2.2). A partir daí o sistema passa a saber o que esperar e mostra o
 * esperado explicitamente:
 *   - passo 2: o LPN que está NA ORIGEM (um só → cartão com o código; vários →
 *     a lista). Casamento por IGUALDADE EXATA (`CodesMatch`).
 *   - passo 3 depois de "registrar ocupado": o STG da rua de origem vira
 *     esperado e tem que ser bipado no chão (§6.2).
 * Bipe que não casa = TELA VERMELHA com ESPERADO × LIDO. Nunca é terminal:
 * sempre dá para tentar de novo, e dá para abrir um incidente SCAN_MISMATCH.
 *
 * ── Por que existe o passo de escolher na torre ─────────────────────────────
 * A única etiqueta física do rack é a do BLOCO (§4): andar e posição não têm
 * etiqueta. Então bipar um rack devolve um BLOCO, e quem diz qual das posições
 * daquela coluna é a certa é o operador, tocando na torre. Fora do rack
 * (staging, doca, fábrica) a etiqueta é o endereço inteiro e esse passo não
 * acontece.
 */

const STEP_ORIGIN = 0;
const STEP_LPN = 1;
const STEP_DESTINATION = 2;
const STEP_COUNT = 3;

const PHASE = {
    SCAN_ORIGIN: 'SCAN_ORIGIN',
    PICK_ORIGIN: 'PICK_ORIGIN',
    SCAN_LPN: 'SCAN_LPN',
    SCAN_DESTINATION: 'SCAN_DESTINATION',
    PICK_DESTINATION: 'PICK_DESTINATION',
    CONFIRM: 'CONFIRM',
    MISMATCH: 'MISMATCH',
    BLOCKED: 'BLOCKED',
    DONE: 'DONE',
};

const INITIAL_STATE = {
    phase: PHASE.SCAN_ORIGIN,
    origin: null,
    originLpns: [],
    lpn: null,
    lpnItems: [],
    destination: null,
    destinationExpectedCode: null,
    // Torre do bloco do DESTINO, guardada para a tela de conferência acender o
    // alvo. Fica separada de `pick` (que é o picker ativo e some ao escolher).
    destinationTower: null,
    pick: null,
    mismatch: null,
    blocks: [],
    movement: null,
    typedInChain: false,
};

const MovePalletFlow = ({ label, operator, equipmentCode = null, onExit }) => {
    const toast = useToast();
    const [state, setState] = useState(INITIAL_STATE);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState(null);

    const plain = useCallback((location) => FormatLocationPlain(location, { label }), [label]);
    const patch = useCallback((changes) => setState((current) => ({ ...current, ...changes })), []);

    const succeed = useCallback((message, changes) => {
        PlayScanSuccess();
        setFlash(message);
        setState((current) => ({ ...current, ...changes }));
    }, []);

    const fail = useCallback((mismatch) => {
        PlayScanError();
        setState((current) => ({ ...current, phase: PHASE.MISMATCH, mismatch }));
    }, []);

    /** Classifica um código no backend (`POST /warehouse/scan`). */
    const classify = useCallback(
        async (code) => {
            const payload = await api.scanWarehouseCode(code);
            return ParseScanResult(payload);
        },
        [],
    );

    const rememberMethod = useCallback((scanMethod) => {
        if (scanMethod === SCAN_METHOD_TYPED) {
            setState((current) => ({ ...current, typedInChain: true }));
        }
    }, []);

    // ── Passo 1 — ORIGEM ────────────────────────────────────────────────────
    const handleOriginScan = useCallback(
        async (code, scanMethod) => {
            setBusy(true);
            rememberMethod(scanMethod);
            try {
                const parsed = await classify(code);
                if (parsed.type === SCAN_TYPE_LOCATION && parsed.location) {
                    succeed(label('wms.collector.flash_origin'), {
                        phase: PHASE.SCAN_LPN,
                        origin: parsed.location,
                        originLpns: parsed.lpns,
                    });
                    return;
                }
                if (parsed.type === SCAN_TYPE_BLOCK && parsed.block) {
                    succeed(label('wms.collector.flash_block'), {
                        phase: PHASE.PICK_ORIGIN,
                        pick: { ...parsed.block, positions: parsed.positions },
                    });
                    return;
                }
                fail({
                    title: label('wms.collector.mismatch_type_title'),
                    message: label('wms.collector.mismatch_expected_location'),
                    read: code,
                    retryPhase: PHASE.SCAN_ORIGIN,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    retryPhase: PHASE.SCAN_ORIGIN,
                });
            } finally {
                setBusy(false);
            }
        },
        [classify, fail, label, rememberMethod, succeed],
    );

    /** Toque numa posição da torre — vale para origem e destino. */
    const handlePickPosition = useCallback(
        async (row, purpose) => {
            setBusy(true);
            try {
                // Reclassifica a POSIÇÃO escolhida: é o que traz os LPNs dela
                // (a linha da torre traz ocupação, não conteúdo).
                const parsed = await classify(row.code);
                const location = parsed.location || row;
                if (purpose === 'ORIGIN') {
                    succeed(label('wms.collector.flash_origin'), {
                        phase: PHASE.SCAN_LPN,
                        origin: location,
                        originLpns: parsed.lpns,
                        pick: null,
                    });
                } else {
                    succeed(label('wms.collector.flash_destination'), {
                        phase: PHASE.CONFIRM,
                        destination: location,
                        destinationTower: state.pick,
                        pick: null,
                    });
                }
            } catch (error) {
                toast.error(error);
            } finally {
                setBusy(false);
            }
        },
        [classify, label, state.pick, succeed, toast],
    );

    // ── Passo 2 — LPN ───────────────────────────────────────────────────────
    const handleLpnScan = useCallback(
        async (code, scanMethod) => {
            const expectedCodes = state.originLpns.map((row) => row.code).filter(Boolean);
            rememberMethod(scanMethod);

            if (expectedCodes.length > 0 && !expectedCodes.some((expected) => CodesMatch(expected, code))) {
                fail({
                    title: label('wms.collector.mismatch_lpn_title'),
                    message: label('wms.collector.mismatch_lpn_hint'),
                    expected: expectedCodes.join('  ·  '),
                    read: code,
                    retryPhase: PHASE.SCAN_LPN,
                    canReportMismatch: true,
                });
                return;
            }

            setBusy(true);
            try {
                const parsed = await classify(code);
                if (parsed.type !== SCAN_TYPE_LPN || !parsed.lpn) {
                    fail({
                        title: label('wms.collector.mismatch_type_title'),
                        message: label('wms.collector.mismatch_expected_lpn'),
                        read: code,
                        retryPhase: PHASE.SCAN_LPN,
                        canReportMismatch: true,
                    });
                    return;
                }
                // O backend é a verdade sobre ONDE o LPN está: se ele diz outra
                // posição, o dado do armazém está errado — e isso é exatamente
                // um incidente, não um "deixa passar".
                const lpnLocationCode = parsed.lpn.location_code || parsed.lpn.location?.code;
                if (lpnLocationCode && state.origin?.code && !CodesMatch(state.origin.code, lpnLocationCode)) {
                    fail({
                        title: label('wms.collector.mismatch_place_title'),
                        message: label('wms.collector.mismatch_place_hint'),
                        expected: state.origin.code,
                        read: lpnLocationCode,
                        retryPhase: PHASE.SCAN_LPN,
                        canReportMismatch: true,
                    });
                    return;
                }
                succeed(label('wms.collector.flash_lpn'), {
                    phase: PHASE.SCAN_DESTINATION,
                    lpn: parsed.lpn,
                    lpnItems: parsed.items,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    retryPhase: PHASE.SCAN_LPN,
                });
            } finally {
                setBusy(false);
            }
        },
        [classify, fail, label, rememberMethod, state.origin, state.originLpns, succeed],
    );

    // ── Passo 3 — DESTINO ───────────────────────────────────────────────────
    const handleDestinationScan = useCallback(
        async (code, scanMethod) => {
            rememberMethod(scanMethod);

            // Depois do "registrar ocupado" o destino deixa de ser escolha: o
            // STG da rua é o esperado e tem que ser bipado no chão (§6.2).
            if (state.destinationExpectedCode && !CodesMatch(state.destinationExpectedCode, code)) {
                fail({
                    title: label('wms.collector.mismatch_destination_title'),
                    message: label('wms.collector.mismatch_staging_hint'),
                    expected: state.destinationExpectedCode,
                    read: code,
                    retryPhase: PHASE.SCAN_DESTINATION,
                    canReportMismatch: true,
                });
                return;
            }

            setBusy(true);
            try {
                const parsed = await classify(code);
                if (parsed.type === SCAN_TYPE_LOCATION && parsed.location) {
                    succeed(label('wms.collector.flash_destination'), {
                        phase: PHASE.CONFIRM,
                        destination: parsed.location,
                    });
                    return;
                }
                if (parsed.type === SCAN_TYPE_BLOCK && parsed.block && !state.destinationExpectedCode) {
                    succeed(label('wms.collector.flash_block'), {
                        phase: PHASE.PICK_DESTINATION,
                        pick: { ...parsed.block, positions: parsed.positions },
                    });
                    return;
                }
                fail({
                    title: label('wms.collector.mismatch_type_title'),
                    message: label('wms.collector.mismatch_expected_location'),
                    read: code,
                    retryPhase: PHASE.SCAN_DESTINATION,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    retryPhase: PHASE.SCAN_DESTINATION,
                });
            } finally {
                setBusy(false);
            }
        },
        [classify, fail, label, rememberMethod, state.destinationExpectedCode, succeed],
    );

    // ── Gravar o movimento ──────────────────────────────────────────────────
    const handleConfirm = useCallback(async () => {
        if (!state.lpn?.code || !state.destination?.code) {
            return;
        }
        setBusy(true);
        try {
            const movement = await api.createWarehouseMovement({
                lpn_code: state.lpn.code,
                to_location_code: state.destination.code,
                from_location_code: state.origin?.code ?? undefined,
                // A trilha não pode dizer SCANNED se QUALQUER leitura da cadeia
                // foi digitada — o valor honesto é o pior caso da cadeia.
                scan_method: state.typedInChain ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED,
                // Equipamento da SESSÃO (escolhido ao entrar no coletor): é o
                // que faz `equipment.last_position` andar de verdade (§6.4) —
                // sem ele o movimento acontece, mas a última posição não muda.
                equipment_code: equipmentCode || undefined,
                // QUEM FEZ: o crachá identificado ao abrir a sessão. A conta do
                // coletor é COMPARTILHADA — sem este campo o backend cai no
                // vínculo do usuário logado, que no dispositivo não existe (o
                // movimento morria em 400) ou, pior, atribuiria a uma pessoa só
                // tudo que o galpão inteiro fez.
                operator_code: operator?.employee_code || undefined,
            });
            PlayMovementDone();
            patch({ phase: PHASE.DONE, movement: movement || {} });
        } catch (error) {
            if (error?.status === 409) {
                PlayScanError();
                patch({ phase: PHASE.BLOCKED, blocks: ParseEngineBlocks(error) });
            } else {
                PlayScanError();
                toast.error(error);
            }
        } finally {
            setBusy(false);
        }
    }, [equipmentCode, operator, patch, state.destination, state.lpn, state.origin, state.typedInChain, toast]);

    // ── Incidentes abertos pelo coletor ─────────────────────────────────────
    // ⚠️ O vínculo do incidente vai por CÓDIGO (`location_code`/`lpn_code`) —
    // é o contrato de POST /warehouse/incidents, o mesmo do movimento. Enquanto
    // isto mandava `location_id`/`lpn_id`, o backend descartava as chaves
    // desconhecidas e TODO incidente aberto no coletor chegava ao painel sem
    // posição e sem LPN: o Supervisor recebia "ocupação não cadastrada" sem
    // saber ONDE.
    const openIncident = useCallback(
        async (kind, { locationCode = null, lpnCode = null, notes = '' } = {}) => {
            // QUEM REPORTOU vai em campo próprio (`operator_code` → coluna
            // `reported_by_operator_id`), não dentro do texto: numa auditoria
            // meses depois, autoria enterrada em nota livre não se filtra nem
            // se soma — e a conta que grava é a do dispositivo, não a da pessoa.
            const payload = { kind, notes: notes || null, operator_code: operator?.employee_code || undefined };
            if (locationCode) {
                payload.location_code = locationCode;
            }
            if (lpnCode) {
                payload.lpn_code = lpnCode;
            }
            return api.createWarehouseIncident(payload);
        },
        [operator],
    );

    const handleReportMismatch = useCallback(async () => {
        setBusy(true);
        try {
            await openIncident(INCIDENT_KIND_SCAN_MISMATCH, {
                locationCode: state.origin?.code ?? null,
                lpnCode: state.lpn?.code ?? null,
                // O crachá SAIU do texto (vai em `operator_code`): a nota volta a
                // ser só o fato lido no chão — esperado × lido.
                notes: `${label('wms.collector.expected')}: ${state.mismatch?.expected ?? '—'} | ${label('wms.collector.read')}: ${state.mismatch?.read ?? '—'}`,
            });
            toast.success(label('wms.collector.incident_created'));
            patch({ phase: state.mismatch?.retryPhase || PHASE.SCAN_ORIGIN, mismatch: null });
        } catch (error) {
            toast.error(error);
        } finally {
            setBusy(false);
        }
    }, [label, openIncident, patch, state.lpn, state.mismatch, state.origin, toast]);

    const handleReportMissingPallet = useCallback(async () => {
        setBusy(true);
        try {
            await openIncident(INCIDENT_KIND_MISSING_PALLET, {
                locationCode: state.origin?.code ?? null,
                notes: `${label('wms.collector.missing_pallet_note')} ${state.origin?.code ?? ''}`.trim(),
            });
            toast.success(label('wms.collector.incident_created'));
            setState(INITIAL_STATE);
        } catch (error) {
            toast.error(error);
        } finally {
            setBusy(false);
        }
    }, [label, openIncident, state.origin, toast]);

    /**
     * "Registrar ocupado" (§6.2): a posição está livre no sistema e ocupada no
     * chão. Abre o incidente NA POSIÇÃO e desvia o pallet para a CABECEIRA do
     * rack de ORIGEM (STG-A-00) — o operador não fica parado esperando o Supervisor.
     */
    const handleRegisterOccupied = useCallback(async () => {
        // Rack é LETRA (005): sem rack na origem não há cabeceira para desviar.
        const rack = state.origin?.rack ? String(state.origin.rack).toUpperCase() : null;
        if (!rack) {
            toast.error(label('wms.collector.occupied_no_street'));
            return;
        }
        setBusy(true);
        try {
            await openIncident(INCIDENT_KIND_UNREGISTERED_OCCUPANCY, {
                locationCode: state.destination?.code ?? null,
                lpnCode: state.lpn?.code ?? null,
                notes: `${label('wms.collector.occupied_note')} ${state.destination?.code ?? ''}`.trim(),
            });
            const stagingCode = BuildRackStagingCode(rack);
            toast.success(label('wms.collector.incident_created'));
            patch({
                phase: PHASE.SCAN_DESTINATION,
                destination: null,
                destinationTower: null,
                destinationExpectedCode: stagingCode,
            });
        } catch (error) {
            toast.error(error);
        } finally {
            setBusy(false);
        }
    }, [label, openIncident, patch, state.destination, state.lpn, state.origin, toast]);

    const restart = useCallback(() => setState(INITIAL_STATE), []);

    // ── Telas ───────────────────────────────────────────────────────────────
    const flashNode = <ScanFlash message={flash} onDone={() => setFlash(null)} />;

    if (state.phase === PHASE.MISMATCH) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={state.mismatch?.title}
                    message={state.mismatch?.message}
                    expected={state.mismatch?.expected}
                    read={state.mismatch?.read}
                    actions={
                        <>
                            <BigAction
                                variant="secondary"
                                onClick={() => patch({ phase: state.mismatch?.retryPhase || PHASE.SCAN_ORIGIN, mismatch: null })}
                            >
                                {label('wms.collector.try_again')}
                            </BigAction>
                            {state.mismatch?.canReportMismatch ? (
                                <DangerScreenAction onClick={handleReportMismatch} disabled={busy}>
                                    {label('wms.collector.report_mismatch')}
                                </DangerScreenAction>
                            ) : null}
                            <DangerScreenAction onClick={restart}>
                                {label('wms.collector.restart_flow')}
                            </DangerScreenAction>
                        </>
                    }
                />
            </>
        );
    }

    if (state.phase === PHASE.BLOCKED) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={label('wms.collector.blocked_title')}
                    message={label('wms.collector.blocked_hint')}
                    reasons={state.blocks}
                    actions={
                        <>
                            <BigAction
                                variant="secondary"
                                onClick={() =>
                                    patch({
                                        phase: PHASE.SCAN_DESTINATION,
                                        destination: null,
                                        destinationTower: null,
                                        blocks: [],
                                        destinationExpectedCode: null,
                                    })
                                }
                            >
                                {label('wms.collector.pick_other_destination')}
                            </BigAction>
                            <DangerScreenAction onClick={restart}>
                                {label('wms.collector.restart_flow')}
                            </DangerScreenAction>
                        </>
                    }
                />
            </>
        );
    }

    if (state.phase === PHASE.DONE) {
        return (
            <>
                {flashNode}
                <SuccessScreen
                    title={label('wms.collector.move_done_title')}
                    actions={
                        <>
                            <BigAction onClick={restart}>{label('wms.collector.new_movement')}</BigAction>
                            <Button variant="secondary" size="lg" className="w-full" onClick={onExit}>
                                {label('wms.collector.back_to_menu')}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3">
                        <div className="rounded-xl bg-white/70 px-4 py-3 text-center dark:bg-mono-950/60">
                            <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                {label('wms.collector.lpn_label')}
                            </p>
                            <Mono className="block break-all text-2xl font-black text-black dark:text-white">
                                {state.lpn?.code}
                            </Mono>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1 rounded-xl bg-white/70 px-3 py-3 text-center dark:bg-mono-950/60">
                                <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                    {label('wms.collector.from')}
                                </p>
                                <p className="text-base font-bold text-black dark:text-white">{plain(state.origin)}</p>
                                <CodeLine className="text-xs">{state.origin?.code}</CodeLine>
                            </div>
                            <ArrowRight size={28} className="shrink-0 text-success-700 dark:text-success-400" />
                            <div className="min-w-0 flex-1 rounded-xl bg-white/70 px-3 py-3 text-center dark:bg-mono-950/60">
                                <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                    {label('wms.collector.to')}
                                </p>
                                <p className="text-base font-bold text-black dark:text-white">
                                    {plain(state.destination)}
                                </p>
                                <CodeLine className="text-xs">{state.destination?.code}</CodeLine>
                            </div>
                        </div>
                        <p className="text-center text-sm text-mono-600 dark:text-mono-400">
                            {`${label('wms.collector.scan_method')}: `}
                            <Badge variant={state.typedInChain ? 'warning' : 'success'}>
                                {label(
                                    `wms.collector.scan_method.${state.typedInChain ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED}`,
                                )}
                            </Badge>
                        </p>
                    </div>
                </SuccessScreen>
            </>
        );
    }

    // ── Passos com bipe/escolha ─────────────────────────────────────────────
    const stepIndex =
        state.phase === PHASE.SCAN_ORIGIN || state.phase === PHASE.PICK_ORIGIN
            ? STEP_ORIGIN
            : state.phase === PHASE.SCAN_LPN
              ? STEP_LPN
              : STEP_DESTINATION;

    const stepName = `${label('wms.collector.step')} ${stepIndex + 1}/${STEP_COUNT} · ${label(
        `wms.collector.step_name.${['ORIGIN', 'LPN', 'DESTINATION'][stepIndex]}`,
    )}`;

    const backButton = (
        <Button variant="ghost" size="lg" className="w-full" onClick={stepIndex === STEP_ORIGIN ? onExit : restart}>
            <ArrowLeft size={20} className="mr-2" />
            {stepIndex === STEP_ORIGIN ? label('wms.collector.back_to_menu') : label('wms.collector.restart_flow')}
        </Button>
    );

    // Rodapé fixo = as AÇÕES do passo. Nos passos de bipe a única ação é sair/
    // recomeçar (quem age é o leitor); na conferência é onde ficam as três
    // saídas possíveis — gravar, registrar ocupado, trocar de destino.
    const footer =
        state.phase === PHASE.CONFIRM ? (
            <>
                <BigAction onClick={handleConfirm} loading={busy}>
                    {label('wms.collector.confirm_move')}
                </BigAction>
                <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    onClick={handleRegisterOccupied}
                    loading={busy}
                >
                    <Warning size={20} className="mr-2" />
                    {label('wms.collector.register_occupied')}
                </Button>
                <Button
                    variant="ghost"
                    size="lg"
                    className="w-full"
                    onClick={() =>
                        patch({
                            phase: PHASE.SCAN_DESTINATION,
                            destination: null,
                            destinationTower: null,
                            destinationExpectedCode: null,
                        })
                    }
                >
                    {label('wms.collector.pick_other_destination')}
                </Button>
            </>
        ) : (
            backButton
        );

    const originBanner = state.origin ? (
        <div className="rounded-xl bg-mono-50 px-3 py-2 text-center dark:bg-mono-900">
            <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                {label('wms.collector.from')}
            </p>
            <p className="text-base font-bold text-black dark:text-white">{plain(state.origin)}</p>
            <CodeLine className="text-xs">{state.origin.code}</CodeLine>
        </div>
    ) : null;

    return (
        <>
            {flashNode}
            <StepShell stepIndex={stepIndex} stepCount={STEP_COUNT} stepName={stepName} footer={footer}>
                {/* ---------- 1. ORIGEM ---------- */}
                {state.phase === PHASE.SCAN_ORIGIN ? (
                    <>
                        <PlainAddress>{label('wms.collector.step_origin_title')}</PlainAddress>
                        <p className="text-center text-base text-mono-600 dark:text-mono-400">
                            {label('wms.collector.step_origin_hint')}
                        </p>
                        <ScanInput
                            label={label}
                            onScan={handleOriginScan}
                            disabled={busy}
                            focusKey={state.phase}
                            manualPlaceholder="R02-B10"
                        />
                    </>
                ) : null}

                {state.phase === PHASE.PICK_ORIGIN && state.pick ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.collector.pick_origin_title')}
                        </h3>
                        <PlainAddress>
                            {`${label('wms.field.rack')} ${state.pick.rack}, ${label('wms.field.block')} ${state.pick.bay}`}
                        </PlainAddress>
                        <CodeLine>{state.pick.code}</CodeLine>
                        <BlockTower
                            rack={state.pick.rack}
                            bay={state.pick.bay}
                            positions={state.pick.positions}
                            label={label}
                            onPick={(row) => handlePickPosition(row, 'ORIGIN')}
                            pickFilter="OCCUPIED"
                        />
                    </>
                ) : null}

                {/* ---------- 2. LPN ---------- */}
                {state.phase === PHASE.SCAN_LPN ? (
                    <>
                        {originBanner}
                        <PlainAddress>{label('wms.collector.step_lpn_title')}</PlainAddress>
                        {state.originLpns.length === 0 ? (
                            <div className="space-y-3 rounded-2xl border-2 border-warning-400 bg-warning-soft p-4 text-center dark:border-warning-700 dark:bg-warning-900/30">
                                <Warning size={40} className="mx-auto text-warning-text dark:text-warning-300" />
                                <p className="text-lg font-bold text-warning-text dark:text-warning-200">
                                    {label('wms.collector.no_lpn_here_title')}
                                </p>
                                <p className="text-sm text-warning-text dark:text-warning-300">
                                    {label('wms.collector.no_lpn_here_hint')}
                                </p>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    className="w-full"
                                    onClick={handleReportMissingPallet}
                                    loading={busy}
                                >
                                    {label('wms.collector.report_missing_pallet')}
                                </Button>
                            </div>
                        ) : (
                            // Regra do dono: a tela NUNCA mostra o código que
                            // espera — nem o LPN da posição. O operador bipa a
                            // etiqueta física e o sistema diz certo/errado.
                            <p className="text-center text-base font-medium text-mono-600 dark:text-mono-400">
                                {state.originLpns.length} {label('wms.collector.lpns_here')}
                            </p>
                        )}
                        <ScanInput
                            label={label}
                            onScan={handleLpnScan}
                            disabled={busy || state.originLpns.length === 0}
                            focusKey={state.phase}
                            manualPlaceholder=""
                        />
                    </>
                ) : null}

                {/* ---------- 3. DESTINO ---------- */}
                {state.phase === PHASE.SCAN_DESTINATION ? (
                    <>
                        {originBanner}
                        <PlainAddress>{label('wms.collector.step_destination_title')}</PlainAddress>
                        {state.destinationExpectedCode ? (
                            <>
                                <p className="text-center text-base font-medium text-warning-text dark:text-warning-300">
                                    {label('wms.collector.redirected_to_staging')}
                                </p>
                                {/* Instrução de DESTINO (para onde ir) — não é a
                                    resposta de uma verificação. */}
                                <CodeCard caption={label('wms.collector.take_to')}>
                                    {state.destinationExpectedCode}
                                </CodeCard>
                            </>
                        ) : (
                            <p className="text-center text-base text-mono-600 dark:text-mono-400">
                                {label('wms.collector.step_destination_hint')}
                            </p>
                        )}
                        <ScanInput
                            label={label}
                            onScan={handleDestinationScan}
                            disabled={busy}
                            focusKey={`${state.phase}-${state.destinationExpectedCode || ''}`}
                            manualPlaceholder=""
                        />
                    </>
                ) : null}

                {state.phase === PHASE.PICK_DESTINATION && state.pick ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.collector.pick_destination_title')}
                        </h3>
                        <PlainAddress>
                            {`${label('wms.field.rack')} ${state.pick.rack}, ${label('wms.field.block')} ${state.pick.bay}`}
                        </PlainAddress>
                        <CodeLine>{state.pick.code}</CodeLine>
                        <BlockTower
                            rack={state.pick.rack}
                            bay={state.pick.bay}
                            positions={state.pick.positions}
                            label={label}
                            onPick={(row) => handlePickPosition(row, 'DESTINATION')}
                            pickFilter="FREE"
                        />
                    </>
                ) : null}

                {/* ---------- Conferência ---------- */}
                {state.phase === PHASE.CONFIRM ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.collector.confirm_title')}
                        </h3>
                        <CodeCard caption={label('wms.collector.lpn_label')} tone="success">
                            {state.lpn?.code}
                        </CodeCard>
                        <div className="space-y-1">
                            <p className="text-center text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                {label('wms.collector.to')}
                            </p>
                            <PlainAddress>{plain(state.destination)}</PlainAddress>
                            <CodeLine>{state.destination?.code}</CodeLine>
                        </div>
                        {state.destinationTower?.positions?.length ? (
                            <BlockTower
                                rack={state.destinationTower.rack}
                                bay={state.destinationTower.bay}
                                positions={state.destinationTower.positions}
                                targetCode={state.destination?.code}
                                label={label}
                            />
                        ) : null}
                        {state.lpnItems.length > 0 ? (
                            <ul className="space-y-1 rounded-xl bg-mono-50 px-3 py-2 dark:bg-mono-900">
                                {state.lpnItems.map((item, index) => (
                                    <li
                                        key={item.id || `${item.sku}-${index}`}
                                        className="flex items-center justify-between gap-3 text-sm"
                                    >
                                        <Mono className="truncate text-mono-700 dark:text-mono-300">{item.sku}</Mono>
                                        <Mono className="shrink-0 font-bold text-black dark:text-white">
                                            {FormatInt(item.qty ?? 0)}
                                        </Mono>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </>
                ) : null}
            </StepShell>
        </>
    );
};

export default MovePalletFlow;
